import React from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';

/**
 * The smile picker (spec 45 §1, board INV13SmilePicker). A 4×2 grid of the eight
 * Smile-Quiz line drawings. Tap the closest; tap another if two apply. The first
 * tapped is the **main** one (tag MAIN); the rest show a tick. Tapping a picked
 * card again unpicks it. Controlled — the caller owns the picked array (first =
 * main). Used as a modal on the patient tab and as a step in the log (§1.2).
 */

export interface InvSmileType {
  name: string;
  slug: string;
  desc: string;
}

/** The eight types, in the quiz's order. The drawings live in public/invisalign. */
export const INV_SMILE_TYPES: InvSmileType[] = [
  { name: 'Overbite', slug: 'overbite', desc: 'Upper front teeth cover most of the lower ones.' },
  { name: 'Underbite', slug: 'underbite', desc: 'Lower front teeth sit in front of the upper ones.' },
  { name: 'Crossbite', slug: 'crossbite', desc: 'Some upper teeth sit inside the lower teeth.' },
  { name: 'Gap teeth', slug: 'gap-teeth', desc: 'Spaces between two or more teeth.' },
  { name: 'Open bite', slug: 'open-bite', desc: "Front teeth don't meet when biting." },
  { name: 'Crooked teeth', slug: 'crooked-teeth', desc: 'Teeth overlap or sit at angles.' },
  { name: 'Generally straight', slug: 'generally-straight', desc: 'Only small things to tidy.' },
  { name: 'Mixed baby teeth', slug: 'mixed-baby-teeth', desc: 'Baby and adult teeth together.' },
];

const SLUG_BY_NAME: Record<string, string> = Object.fromEntries(INV_SMILE_TYPES.map((t) => [t.name, t.slug]));

/** The drawing for a smile-type name (used by the tab tiles too). */
export function smileImageSrc(name: string): string {
  return `/invisalign/${SLUG_BY_NAME[name] ?? 'crooked-teeth'}.png`;
}

/** The two special-case lines shown under the grid (spec 30 §5, kept in §1.1). */
export function smileSpecialLine(picked: string[]): string | null {
  if (picked.length === 1 && picked[0] === 'Generally straight') return "No emails — there's nothing to straighten.";
  if (picked.includes('Mixed baby teeth')) return 'Parked until they’re ready. Emails will go to the parent.';
  return null;
}

export interface InvSmilePickerProps {
  /** Picked names; the first is the main one (tag MAIN). */
  value: string[];
  onChange: (next: string[]) => void;
  className?: string;
}

export function InvSmilePicker({ value, onChange, className }: InvSmilePickerProps) {
  const toggle = (name: string) => {
    onChange(value.includes(name) ? value.filter((v) => v !== name) : [...value, name]);
  };
  const special = smileSpecialLine(value);

  return (
    <div className={className}>
      <div className="grid grid-cols-4 gap-2.5 max-[560px]:grid-cols-2">
        {INV_SMILE_TYPES.map((t) => {
          const idx = value.indexOf(t.name);
          const picked = idx !== -1;
          const main = idx === 0;
          return (
            <button
              key={t.slug}
              type="button"
              onClick={() => { toggle(t.name); }}
              aria-pressed={picked}
              className={cn(
                'relative flex flex-col items-center rounded-[14px] border p-2.5 text-center transition-[border-color,box-shadow]',
                picked
                  ? 'border-[var(--brand-indigo)] shadow-[0_0_0_2px_var(--brand-indigo-tint)]'
                  : 'border-[var(--border-default)] hover:border-[#ccd4e4]',
              )}
            >
              {picked ? (
                <span
                  className={cn(
                    'absolute right-1.5 top-1.5 inline-flex items-center rounded-full px-1.5 py-px text-[9px] font-bold uppercase tracking-[0.04em]',
                    main ? 'bg-[var(--brand-indigo)] text-white' : 'bg-[var(--brand-indigo-tint)] text-[var(--brand-indigo)]',
                  )}
                >
                  {main ? 'Main' : <Icon kind="check" size={11} stroke={2.4} />}
                </span>
              ) : null}
              <img src={smileImageSrc(t.name)} alt="" className="h-[52px] w-full object-contain" />
              <span className="mt-1.5 text-[12.5px] font-semibold text-[var(--text-primary)]">{t.name}</span>
              <span className="mt-0.5 text-[10.5px] leading-[1.35] text-[var(--text-muted)]">{t.desc}</span>
            </button>
          );
        })}
      </div>
      {special ? (
        <div className="mt-3 rounded-[10px] bg-[var(--surface-sunken)] px-3 py-2 text-[12px] text-[var(--text-secondary)]">{special}</div>
      ) : null}
    </div>
  );
}

/** The picked types as small tiles (drawing + name, MAIN on the first) — the tab row. */
export function InvSmileTiles({ picked }: { picked: string[] }) {
  return (
    <div className="flex flex-wrap gap-2">
      {picked.map((name, i) => (
        <span key={name} className="inline-flex items-center gap-1.5 rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-card)] px-2 py-1">
          <img src={smileImageSrc(name)} alt="" className="h-6 w-8 object-contain" />
          <span className="text-[12px] font-semibold text-[var(--text-primary)]">{name}</span>
          {i === 0 ? <span className="rounded-full bg-[var(--brand-indigo)] px-1.5 py-px text-[9px] font-bold uppercase text-white">main</span> : null}
        </span>
      ))}
    </div>
  );
}
